<template>
    <div v-if="series" class="space-y-4">
        <FlexibleCard headline-id="allocationRules" expandable-status="Not-Collapsible">
            <template #header="{ headlineId }">
                <CardHeaderEditable
                    :status="getCardStatus('allocationRules')"
                    :edit-button-disabled-explanation="editButtonDisabledMessage('allocationRules')"
                    @enter-edit-mode="onEnterEditMode('allocationRules', $t('allocation-rules'))"
                    @leave-edit-mode="leaveEditMode('allocationRules')"
                    @discard-changes="discardChanges('allocationRules')"
                    @save="saveForm('allocationRules')"
                >
                    <CardHeadline :headline-id="headlineId">
                        {{ $t('allocation-rules') }}
                    </CardHeadline>
                </CardHeaderEditable>
            </template>

            <template #body>
                <ContentFormTransition>
                    <EventSeriesAllocationRulesForm
                        v-if="showForm('allocationRules')"
                        ref="allocationRulesFormRef"
                        class="p-5"
                        :series-id="series.id"
                    />
                    <div v-else>
                        <CardRowText
                            :label="$t('allocation-rules')"
                            :text="valueToString(series.allocationRules)"
                            :has-border-bottom="true"
                        />
                        <CardRowText
                            :label="$t('location-history')"
                            :text="valueToString(series.locationHistory)"
                            :help-text="$t('location-history-help')"
                            :has-border-bottom="false"
                        />
                    </div>
                </ContentFormTransition>
            </template>
        </FlexibleCard>

        <FlexibleCard headline-id="allocationNeeds" expandable-status="Not-Collapsible">
            <template #header="{ headlineId }">
                <CardHeaderEditable
                    :status="getCardStatus('allocationNeeds')"
                    :edit-button-disabled-explanation="editButtonDisabledMessage('allocationRulesLocationTypes')"
                    @enter-edit-mode="onEnterEditMode('allocationNeeds', $t('allocation-rules-location-types'))"
                    @leave-edit-mode="leaveEditMode('allocationNeeds')"
                    @discard-changes="discardChanges('allocationNeeds')"
                    @save="saveForm('allocationNeeds')"
                >
                    <CardHeadline :headline-id="headlineId">
                        {{ $t('allocation-rules-location-types') }}
                    </CardHeadline>
                </CardHeaderEditable>
            </template>

            <template #body>
                <ContentFormTransition>
                    <EventSeriesAllocationNeedsForm
                        v-if="showForm('allocationNeeds')"
                        ref="allocationNeedsFormRef"
                        class="p-5"
                        :series-id="series.id"
                    />
                    <div v-else>
                        <CardRowText
                            :label="$t('notes-hall-exhibition-areas')"
                            :text="valueToString(series.notesHallExhibitionAreas)"
                            :has-border-bottom="true"
                        />
                        <CardRowText
                            :label="$t('notes-boulevard-sections')"
                            :text="valueToString(series.notesBoulevardSections)"
                            :has-border-bottom="true"
                        />
                        <CardRowText
                            :label="$t('notes-passages')"
                            :text="valueToString(series.notesPassages)"
                            :has-border-bottom="true"
                        />
                        <CardRowText
                            :label="$t('notes-entrances')"
                            :text="valueToString(series.notesEntrances)"
                            :has-border-bottom="true"
                        />
                        <CardRowText
                            :label="$t('notes-service-centers')"
                            :text="valueToString(series.notesServiceCenters)"
                            :has-border-bottom="true"
                        />
                        <CardRowText
                            :label="$t('notes-conference-rooms')"
                            :text="valueToString(series.notesConferenceRooms)"
                            :has-border-bottom="true"
                        />
                        <CardRowText
                            :label="$t('notes-offices-internal')"
                            :text="valueToString(series.notesOfficesInternal)"
                            :has-border-bottom="true"
                        />
                        <CardRowText
                            :label="$t('notes-storage-internal')"
                            :text="valueToString(series.notesStorageInternal)"
                            :has-border-bottom="true"
                        />
                        <CardRowText
                            :label="$t('notes-offices-external')"
                            :text="valueToString(series.notesOfficesExternal)"
                            :has-border-bottom="true"
                        />
                        <CardRowText
                            :label="$t('notes-storage-external')"
                            :text="valueToString(series.notesStorageExternal)"
                            :has-border-bottom="true"
                        />
                        <CardRowText
                            :label="$t('notes-areas')"
                            :text="valueToString(series.notesAreas)"
                            :has-border-bottom="true"
                        />
                        <CardRowText
                            :label="$t('notes-parking')"
                            :text="valueToString(series.notesParking)"
                            :has-border-bottom="true"
                        />
                        <CardRowText
                            :label="$t('other-allocation-notes')"
                            :text="valueToString(series.otherAllocationNeeds)"
                            :has-border-bottom="false"
                        />
                    </div>
                </ContentFormTransition>
            </template>
        </FlexibleCard>
    </div>
</template>

<script setup lang="ts">
import CardHeaderEditable from '@/components/Card/CardHeaderEditable.vue';
import CardHeadline from '@/components/Card/CardHeadline.vue';
import CardRowText from '@/components/Card/CardRowText.vue';
import FlexibleCard from '@/components/Card/FlexibleCard.vue';
import ContentFormTransition from '@/event/components/ContentFormTransition.vue';
import { useEventSeriesInjection } from '@/event/composables/useEventSeriesInjection';
import EventSeriesAllocationNeedsForm from '@/event/forms/series/EventSeriesAllocationNeedsForm.vue';
import EventSeriesAllocationRulesForm from '@/event/forms/series/EventSeriesAllocationRulesForm.vue';
import { useFormRegistry } from '@/event/forms/useFormRegistry';
import { useActiveCard } from '@/forms/composables/useActiveCard';
import { valueToString } from '@/forms/forms';
import { useFluent } from 'fluent-vue';
import { useTemplateRef } from 'vue';
import { onBeforeRouteLeave } from 'vue-router';

defineProps<{ seriesId: string }>();

const { series } = useEventSeriesInjection();

const { activeCard, resetActiveCard, getCardStatus, showForm, editButtonDisabledMessage, onEnterEditMode } =
    useActiveCard();

const fluent = useFluent();

const allocationRulesFormRef = useTemplateRef('allocationRulesFormRef');
const allocationNeedsFormRef = useTemplateRef('allocationNeedsFormRef');

const { register, saveForm, discardChanges, leaveEditMode, resetAll } = useFormRegistry();

register('allocationRules', allocationRulesFormRef);
register('allocationNeeds', allocationNeedsFormRef);

onBeforeRouteLeave(() => {
    if (activeCard.value.id && activeCard.value.status === 'Edit-Touched') {
        if (!confirm(fluent.$t('leave-page-warning'))) {
            return false;
        }
        resetAll();
    }
    resetActiveCard();
    return true;
});
</script>
